import { REJECTED_STATUSES } from 'components/VenueTile/VenueCard';
import { SHORTLIST_STATUSES, VenueStatus } from 'constants/venueStatus';
import { VENUE_ID_CORPORATE_OFFICE } from 'constants/venueTypes';
import sortBy from 'lodash/sortBy';

export const getFilterTypeIds = (filterTypes: BizlyAPI.Venue.Types[] | undefined, playbookTypes: Bizly.VenueType[]) => {
    if (!filterTypes || filterTypes.length === 0) {
        return [];
    }
    const ids = filterTypes.map(filterType => {
        const playbookVenueType = playbookTypes.find(type => type.name.toLowerCase() === filterType.toLowerCase());
        if (playbookVenueType) {
            return playbookVenueType.id;
        }
        return -1;
    });
    return ids;
};

export const getBrandOptions = (
    filterTypes: BizlyAPI.Venue.Types[] | undefined,
    playbookTypes: Bizly.VenueType[],
    brands: Bizly.VenueBrand[]
) => {
    const filterTypeIds = getFilterTypeIds(filterTypes, playbookTypes);
    const brandOptions = brands.map(brand => ({
        id: brand.id,
        name: brand.name,
        disabled:
            filterTypeIds.length > 0 &&
            (brand.venueTypeId === undefined || filterTypeIds.indexOf(brand.venueTypeId) < 0),
    }));

    // move disabled brand options to the end of the list
    const activeBrands = brandOptions.filter(brand => !brand.disabled);
    const disabledBrands = brandOptions.filter(brand => brand.disabled);
    return [...activeBrands, ...disabledBrands];
};

export const hasContract = (venue?: Bizly.Venue) => {
    if (venue) {
        if (venue.status === 'Contract Pending' || venue.status === 'Booked' || venue.status === 'Booking Cancelled') {
            return true;
        }
    }
    return false;
};

/** Extracts the actual venue ID regardless of whether this is a search venue or inquiry venue (join row). */
export const getVenueId = (venue: Bizly.Venue): number => venue.venue?.id ?? venue.id;

/** Venues that occupy an inquiry or queue slot (shortlist + Queued). Used for 4+4 limit in useVenueInquiry. */
export const getActiveVenues = (venues: Bizly.Venue[]) =>
    venues.filter(venue => SHORTLIST_STATUSES.has(venue.status as VenueStatus) || venue.status === VenueStatus.Queued);

export const getActiveVenuesCount = (venues: Bizly.Venue[]) => getActiveVenues(venues).length;

export const getSelectedVenueInqs = (venues: Bizly.Venue[]) =>
    venues.filter(venue => !REJECTED_STATUSES.has(venue.status));

export const isUrlFromSubNav = (pathname: string): boolean => {
    return /\/event\/\d+\/?(venue\/?)?/.test(pathname);
};

export const isVenueBookable = (venue?: Bizly.Venue) => {
    if (!venue) return false;
    return !(venue.externalUrl || venue.type?.id === VENUE_ID_CORPORATE_OFFICE);
};

export const canInteractWithVenue = (venue?: Bizly.Venue) => {
    if (venue?.type?.id === VENUE_ID_CORPORATE_OFFICE) return true;
    return isVenueBookable(venue);
};

export const getValidInquiryVenues = (currentInquiryVenues: Bizly.Venue[]): Bizly.Venue[] => {
    return currentInquiryVenues.filter(v => !REJECTED_STATUSES.has(v.status));
};

/** Count of venues on the shortlist (for Short list button, etc.). Excludes queued and inactive. */
export const getShortlistCount = (venues: Bizly.Venue[]): number =>
    (venues || []).filter(v => SHORTLIST_STATUSES.has(v.status as VenueStatus)).length;

/** Remaining queue slots: inquiry cap minus venues currently in Queued status. */
export const getRemainingQueueSlots = (
    venues: Bizly.Venue[] | null | undefined,
    availableQueueSlots: number | null | undefined
): number => {
    const queuedCount = (venues ?? []).filter(v => v.status === VenueStatus.Queued).length;
    return Math.max(0, (availableQueueSlots ?? 0) - queuedCount);
};

/** Returns true if the venue is favorited by the current user or any team member */
export const isVenueFavorited = (venue: Pick<Bizly.Venue, 'favorite' | 'teamFavorite'>): boolean => {
    return !!venue.favorite || !!venue.teamFavorite;
};

/**
 * Sorts venue search results: Bonfire sponsored first, then by preference category rank.
 * Stable sort preserves server distance order within each tier.
 */
export const sortVenuesForSearchResults = (venues: Bizly.Venue[]): Bizly.Venue[] =>
    sortBy(venues, [venue => (venue.bonfireSponsored ? 0 : 1), venue => venue.preferenceCategory?.rank ?? Infinity]);

type ClientSideVenueFilters = Pick<
    BizlyAPI.VenueFilterValue,
    'favoriteOnly' | 'spacesMin' | 'spacesMax' | 'capacityMin' | 'capacityMax'
>;

/**
 * Applies the filters that can't be pushed to the Algolia request (favorite,
 * spaces count, capacity range) to an already-fetched result set.
 *
 * Capacity range: venues with no `maxCapacity` (e.g. coworking / meeting spaces
 * sourced from Google without a Bizly listing) are INCLUDED rather than dropped.
 * Capacity data is missing for whole categories, so excluding unknown-capacity
 * venues hid every coworking space the moment a range was applied. Users who
 * want a strict cut can hide unknown-capacity venues separately.
 */
export const filterVenuesByClientSideFilters = (
    venues: Bizly.Venue[],
    { favoriteOnly, spacesMin, spacesMax, capacityMin, capacityMax }: ClientSideVenueFilters
): Bizly.Venue[] => {
    let result = venues;

    if (favoriteOnly) {
        result = result.filter(venue => venue.favorite || venue.teamFavorite);
    }

    if (spacesMin != null || spacesMax != null) {
        result = result.filter(venue => {
            // A spaces filter excludes venues with no `spaceTypes` rather than
            // passing them through, otherwise the filter is a no-op for
            // Google-Places venues without a Bizly listing. See BIZ-1927 review.
            const count = (venue.spaceTypes as unknown[])?.length ?? 0;
            if (!count) return false;
            if (spacesMin != null && count < spacesMin) return false;
            if (spacesMax != null && count > spacesMax) return false;
            return true;
        });
    }

    if (capacityMin != null || capacityMax != null) {
        result = result.filter(venue => {
            if (!venue.maxCapacity) return true;
            if (capacityMin != null && venue.maxCapacity < capacityMin) return false;
            if (capacityMax != null && venue.maxCapacity > capacityMax) return false;
            return true;
        });

        // Rank confirmed matches (known maxCapacity within the range) above the
        // unknown-capacity venues we keep but can't confirm. Stable sort preserves the
        // upstream relevance order within each group; this runs after that sort, so it
        // isn't undone.
        result = [...result].sort((a, b) => Number(!a.maxCapacity) - Number(!b.maxCapacity));
    }

    return result;
};

/**
 * Sorts queued venues by queuedAt ascending (oldest first = FIFO order).
 * Venues without a queuedAt value are placed at the end.
 */
export const sortQueuedVenuesByQueuedAt = <T extends Pick<Bizly.Venue, 'queuedAt'>>(venues: T[]): T[] =>
    [...venues].sort((a, b) => {
        if (!a.queuedAt) return 1;
        if (!b.queuedAt) return -1;
        return new Date(a.queuedAt).getTime() - new Date(b.queuedAt).getTime();
    });
